import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { CompanyContext } from '../../context/company/companyContext';
import { PlansContext } from '../../context/plans/plansContext';
import { IFlexibleRemunerationConsumptions, IFlexibleRemunerationPayRollCycle } from '../../interfaces/company';
import Company from '../../services/api/companies/companies';
import { getMonthDateRange } from '../../utils/datetime/datetime';

export const useRFCompanyConsumption = (cycle?: IFlexibleRemunerationPayRollCycle, currentCycle = true) => {
  const [consumptionData, setConsumptionData] = useState<IFlexibleRemunerationConsumptions | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const { company, companyData, isLoadingCompanyData, isLoadingRFCycles, companyRFCycles } = useContext(CompanyContext);
  const { modelType } = useContext(PlansContext);

  const lastFetchRef = useRef<string>('');
  const isFetchingRef = useRef(false);

  useEffect(() => {
    lastFetchRef.current = '';
    isFetchingRef.current = false;
  }, [modelType, companyData.id]);

  const fetchCompanyConsumption = useCallback(
    async (cycle?: IFlexibleRemunerationPayRollCycle, currentCycle = true) => {
      const fetchKey =
        modelType === 'forecast'
          ? `${companyData.id}-forecast-${currentCycle}`
          : `${companyData.id}-${modelType}-${cycle?.id || 'no-cycle'}-${currentCycle}`;

      if (lastFetchRef.current === fetchKey || isFetchingRef.current) {
        return;
      }

      lastFetchRef.current = fetchKey;
      isFetchingRef.current = true;

      setError(false);
      setLoading(true);
      setConsumptionData(undefined);

      try {
        let cycleId: string | undefined;
        let start: string | undefined;
        let end: string | undefined;

        if (modelType === 'forecast') {
          const openCycle = companyRFCycles?.payRollCycles[0];

          if (!openCycle?.endDay) {
            lastFetchRef.current = '';
            isFetchingRef.current = false;
            setLoading(false);
            return;
          }

          const endDate = new Date(openCycle.endDay);
          const cutoffDay = endDate.getUTCDate();

          if (currentCycle) {
            const monthRange = getMonthDateRange(cutoffDay);
            start = monthRange.startDate;
            end = monthRange.endDate;
          } else {
            cycleId = openCycle?.id;

            if (!cycleId) {
              lastFetchRef.current = '';
              isFetchingRef.current = false;
              setLoading(false);
              return;
            }
          }
        } else {
          cycleId = cycle?.id;
        }

        const response = await Company.getCompanyFlexibleRemunerationConsumptions(companyData.id, cycleId, start, end);
        setConsumptionData(response);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [companyData.id, modelType, companyRFCycles]
  );

  useEffect(() => {
    const isValidCompany = companyData && companyData.id !== '' && companyData.id === company.id;

    if (!isValidCompany || isLoadingCompanyData || isLoadingRFCycles) {
      return;
    }

    const shouldFetch = modelType === 'forecast' ? !!companyRFCycles : !!cycle?.id;

    if (shouldFetch) {
      fetchCompanyConsumption(cycle, currentCycle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyData.id,
    company.id,
    isLoadingCompanyData,
    cycle?.id,
    isLoadingRFCycles,
    modelType,
    currentCycle,
    companyRFCycles
  ]);

  return { consumptionData, loading, error, refetch: fetchCompanyConsumption };
};
